export const generateReport = (bonusAmount, loans, selectedStrategy) => {
    let bonus = parseFloat(bonusAmount);
    let newLoans = JSON.parse(JSON.stringify(loans));

    let utilizedBonus = 0;
    let unutilizedBonus = bonus;
    let totalInterestPayableOnOriginalLoan = 0;

    newLoans.forEach(newLoan => {
        const emi = calculateEMI(newLoan.outstandingAmount, newLoan.interestRate, newLoan.remainingTerm);
        const interestPayable = emi * newLoan.remainingTerm - newLoan.outstandingAmount;
        const aipr = ((interestPayable) / newLoan.outstandingAmount) * 100;
        newLoan.emi = emi;
        newLoan.interestPayable = interestPayable;
        newLoan.aipr = aipr;
        totalInterestPayableOnOriginalLoan = totalInterestPayableOnOriginalLoan + interestPayable;
    });

    newLoans = newLoans.filter(loan => loan.isInterestedInLoanPayment);

    while (utilizedBonus < bonus) {

        let loanWithHighestAIPR = null;
        let loansWithOSBalance = newLoans.filter(loan => loan.outstandingAmount > 0);
        loansWithOSBalance = loansWithOSBalance.sort((a, b) => a.aipr - b.aipr);

        if (loansWithOSBalance && loansWithOSBalance.length > 0) {
            if (selectedStrategy === 'Pay loans with highest AIPR (Recommended)' || selectedStrategy === 'Most Savings on Interest (Recommended)' ) {
                loanWithHighestAIPR = loansWithOSBalance.reduce((max, loan) => loan.aipr > max.aipr ? loan : max, newLoans[0]);
            } else if (selectedStrategy === 'Pay loans with lowest o/s balance') {
                loanWithHighestAIPR = loansWithOSBalance.reduce((max, loan) => Number(loan.outstandingAmount) <= Number(max.outstandingAmount) ? loan : max, newLoans[0]);
            } else if (selectedStrategy === 'Pay loans with highest interest rate') {
                loanWithHighestAIPR = loansWithOSBalance.reduce((max, loan) => Number(loan.interestRate) >= Number(max.interestRate) ? loan : max, newLoans[0]);
            }
        }

        if (loanWithHighestAIPR) {
            let bonusShareToBeUtilized = 1000;
            if (unutilizedBonus < bonusShareToBeUtilized || loanWithHighestAIPR.outstandingAmount < bonusShareToBeUtilized) {
                bonusShareToBeUtilized = Math.min(unutilizedBonus, loanWithHighestAIPR.outstandingAmount);
            }
            let prepaymentShare = (loanWithHighestAIPR.prepaymentShare ? loanWithHighestAIPR.prepaymentShare : 0) + bonusShareToBeUtilized;
            let outstandingAmount = loanWithHighestAIPR.outstandingAmount - bonusShareToBeUtilized;
            utilizedBonus = utilizedBonus + bonusShareToBeUtilized;
            unutilizedBonus = bonus - utilizedBonus;

            const interestRate = loanWithHighestAIPR.interestRate;
            const emi = loanWithHighestAIPR.emi;
            const remainingTerm = calculateLoanTenure(outstandingAmount, emi, interestRate);
            const interestPayable = (emi * remainingTerm) - outstandingAmount;
            const aipr = ((interestPayable) / outstandingAmount) * 100;
            loanWithHighestAIPR.outstandingAmount = outstandingAmount;
            loanWithHighestAIPR.prepaymentShare = prepaymentShare;
            loanWithHighestAIPR.remainingTerm = remainingTerm;
            loanWithHighestAIPR.interestPayable = interestPayable;
            loanWithHighestAIPR.aipr = aipr;
        } else {
            break;
        }
    }

    const loansWithPrepaymentShares = newLoans.filter(loan => loan.prepaymentShare && loan.prepaymentShare > 0)
    let reportLoans = [];
    let totalSavingsOnInterest = 0;

    loansWithPrepaymentShares.forEach(loan => {
        const originalLoan = loans.find(originalLoan => originalLoan.id === loan.id);
        const originalLoanEmi = calculateEMI(originalLoan.outstandingAmount, originalLoan.interestRate, originalLoan.remainingTerm);
        const interestPayableOnOriginalLoan = originalLoanEmi * originalLoan.remainingTerm - originalLoan.outstandingAmount;
        reportLoans.push({
            loanType: originalLoan.loanType,
            prepaymentShare: loan.prepaymentShare,
            originalOutstandingAmount: originalLoan.outstandingAmount,
            originalTenure: originalLoan.remainingTerm,
            originalInterestRate: originalLoan.interestRate,
            originalEMI: originalLoanEmi,
            originalInterestPayable: interestPayableOnOriginalLoan,
            adjustedOutstandingAmount: loan.outstandingAmount,
            adjustedTenure: loan.remainingTerm,
            adjustedInterestRate: loan.interestRate,
            adjustedEMI: loan.emi,
            adjustedInterestPayable: loan.interestPayable,
            varianceOutstandingAmount: originalLoan.outstandingAmount - loan.outstandingAmount,
            varianceTenure: originalLoan.remainingTerm - loan.remainingTerm,
            varianceInterestRate: originalLoan.interestRate - loan.interestRate,
            varianceEMI: originalLoanEmi - loan.emi,
            varianceInterestPayable: interestPayableOnOriginalLoan - loan.interestPayable,
        });
        reportLoans = reportLoans.sort((a, b) => b.prepaymentShare - a.prepaymentShare);
        totalSavingsOnInterest = totalSavingsOnInterest + (interestPayableOnOriginalLoan - loan.interestPayable);
    });

    let percentageOfTotalSavingsOnInterest = parseFloat((totalSavingsOnInterest / totalInterestPayableOnOriginalLoan * 100).toFixed(2));

    return {
        reportLoans,
        totalSavingsOnInterest,
        unutilizedBonus,
        percentageOfTotalSavingsOnInterest
    };
};

const calculateLoanTenure = (outstandingAmount, emi, interestRate) => {
    const monthlyRate = interestRate / 12 / 100;
    const numerator = emi / (emi - outstandingAmount * monthlyRate);
    // Calculate the loan tenure in months
    const tenure = Math.log(numerator) / Math.log(1 + monthlyRate);
    // Return the rounded-up tenure in months
    return Math.ceil(tenure);
}

//Calculate monthly payment based on outstanding amount, interest rate, and remaining term
export const calculateEMI = (outstandingAmount, interestRate, remainingTerm) => {
    const monthlyInterestRate = interestRate / 100.00 / 12.00;
    const payment = (outstandingAmount * monthlyInterestRate) / (1 - Math.pow(1 + monthlyInterestRate, -remainingTerm));
    return isNaN(payment) ? 0 : Math.round(payment);
};


export const formatYearsMonths = (months) => {
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    return `${years} year${years !== 1 ? 's' : ''} ${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
};
